import { CompanyResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { UIToast } from '@/ui';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateAllCompaniesCache } from '../queries/company-cache';

type UseDisableSmsActionProps = {
    companyId: string;
};

type UseDisableSmsActionData = {
    disableSms: () => Promise<CompanyResponse>;
    loading: boolean;
    error: string;
};

/**
 * Turning off SMS notifications for the company.
 *
 * Carries no `isAllowed` of its own: the billing tab is gated as a whole by
 * `canEditBilling` on the billing settings page, and everything on it is
 * reachable only from there.
 */
export function useDisableSmsAction(
    props: UseDisableSmsActionProps
): UseDisableSmsActionData {
    const { companyId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/companies/{id}/sms/disable` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'SMS_ALREADY_DISABLED')) {
                return t('actions.disable_sms.error_already_disabled');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.companies
                .disableSms(companyId)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllCompaniesCache(queryClient, data.data.company);

            UIToast.success(t('actions.disable_sms.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        disableSms: (): Promise<CompanyResponse> => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
